import { Icon } from "@fluentui/react";
import { useEffect, useState } from "react";
import ApiService from "../../Services/ApiService";
import { IAPIResponse } from "../../Services/AjaxService";
import { ToastMessage } from "../../Utils/UIMessages";
import { sensorTitle } from "./SetupHelpers";
import {
  Btn,
  CARDS_MAX_WIDTH,
  Chips,
  Dialog,
  Empty,
  ISetupColumn,
  PageHead,
  Pill,
  PillKind,
  SetupCards,
  SetupTable,
  Toolbar,
} from "./SetupUi";

// Scheda Setup > Notifiche: se le email partono e lo storico degli invii, con reinvio.
// Il backend la apre solo ad admin e installatore. Si aggiorna da sola ogni 15 s mentre è aperta (risposta 12).

interface IMailStatus {
  configured: boolean;
  enabled: boolean;
  hasCredentials: boolean;
  host: string | null;
  from: string | null;
  fromName: string | null;
  replyTo: string | null;
}

const REFRESH_MS = 15000;

const STATUS: Record<string, [string, PillKind]> = {
  queued: ["In coda", "grey"],
  sending: ["In invio", "grey"],
  sent: ["Inviata", "ok"],
  failed: ["Fallita", "err"],
  skipped: ["Non inviata", "warn"],
};

const EVENT_TEXT: Record<string, string> = {
  varco: "Allarme varco",
  letto: "Allarme letto",
  alarm: "Allarme",
  test: "Email di prova",
  "test-alarm": "Prova allarme",
};

// tipi dei sensori della stessa email (campo sensors, separati da virgola)
const typesOf = (item: any): string[] =>
  Array.from(
    new Set(
      String(item.sensors ?? "")
        .split(",")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "")
    )
  );

// evento con i nomi dell'app: il letto raggruppato elenca tutti i sensori scattati
const eventText = (item: any) => {
  const types = typesOf(item);
  if (+(item.count ?? 1) > 1 && types.length > 0 && !types.every((t) => t === "door")) return types.map(sensorTitle).join(", ");
  if (types.length > 0 && (item.event === "varco" || item.event === "letto" || item.event === "test-alarm")) return sensorTitle(types[0]);
  // event a volte è il tipo del sensore (door): il nome dell'app
  return EVENT_TEXT[item.event] ?? (sensorTitle(item.event) !== "<?>" ? sensorTitle(item.event) : item.event);
};
const doorOpenings = (item: any) => (+(item.count ?? 1) > 1 && typesOf(item).every((t) => t === "door") ? +item.count : 0);

// date e ore nel fuso dell'impianto
const TIME_ZONE = "Europe/Rome";
const dateParts = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const parts: Record<string, string> = {};
  new Intl.DateTimeFormat("it-IT", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(d)
    .forEach((x) => {
      parts[x.type] = x.value;
    });
  return parts;
};
const dayOf = (iso: string) => {
  const p = dateParts(iso);
  return p ? p.day + "/" + p.month + "/" + p.year : iso ?? "";
};
const timeOf = (iso: string) => {
  const p = dateParts(iso);
  return p ? p.hour + ":" + p.minute + ":" + p.second : "";
};
const whenText = (iso: string) => {
  const p = dateParts(iso);
  return p ? "il " + p.day + "/" + p.month + " alle " + p.hour + ":" + p.minute : "";
};
const nowTime = () => new Date().toTimeString().slice(0, 8);

const isTestRow = (item: any) => item.event === "test-alarm" || item.event === "test";
const isNotSent = (item: any) => item.status === "failed" || item.status === "skipped";
const doneAt = (item: any) => item.sentAt ?? item.updatedAt ?? item.createdAt;

// stato della posta in una riga: partono o no, e perché (senza indirizzi tecnici del server)
const mailVerdict = (mail: IMailStatus | undefined, items: any[]) => {
  if (!mail) return null;
  if (!mail.configured) {
    const why = !mail.enabled
      ? "gli invii sono disattivati per questo impianto"
      : !mail.hasCredentials
      ? "la posta non è configurata (mancano server o credenziali)"
      : "la posta non è configurata";
    return { ok: false, text: "Le email non partono: " + why + ". Le notifiche restano nello storico come «Non inviata»." };
  }
  const finished = items
    .filter((x: any) => x.status === "sent" || x.status === "failed")
    .sort((a: any, b: any) => new Date(doneAt(b)).getTime() - new Date(doneAt(a)).getTime());
  const lastSent = finished.find((x: any) => x.status === "sent");
  if (finished.length > 0 && finished[0].status === "failed") {
    let n = 0;
    while (n < finished.length && finished[n].status === "failed") n++;
    return {
      ok: false,
      text:
        "Le email non partono: " +
        (n > 1 ? "gli ultimi " + n + " invii sono falliti" : "l'ultimo invio è fallito") +
        (finished[0].lastError ? " (" + finished[0].lastError + ")" : "") +
        "." +
        (lastSent ? " Ultimo invio riuscito " + whenText(doneAt(lastSent)) + "." : ""),
    };
  }
  if (lastSent) return { ok: true, text: "Le email partono: ultimo invio riuscito " + whenText(doneAt(lastSent)) + "." };
  return { ok: true, text: "La posta è configurata: nessuna email inviata finora." };
};

const NotificationsTab = (props: { width: number }) => {
  const [mail, setMail] = useState<IMailStatus | undefined>();
  const [items, setItems] = useState<any[]>([]);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [updatedAt, setUpdatedAt] = useState<string>("");
  const [filter, setFilter] = useState<string>("tutti");
  const [search, setSearch] = useState<string>("");
  const [viewed, setViewed] = useState<any | undefined>();

  const load = (quiet: boolean) => {
    ApiService.NotificationController.GetNotifications((response: IAPIResponse) => {
      if (response.error === null) {
        setMail(response.parsed.mail);
        setItems(response.parsed.items);
        setUpdatedAt(nowTime());
      } else if (!quiet) {
        ToastMessage("error", "Impossibile caricare le notifiche.");
      }
      setLoaded(true);
    });
  };

  useEffect(() => {
    load(false);
    const timer = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const q = search.trim().toLowerCase();
  const rows = [...items]
    .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .filter(
      (x: any) =>
        (filter === "tutti" || (filter === "prove" && isTestRow(x)) || (filter === "non" && isNotSent(x))) &&
        (!q || [x.email, x.subject, eventText(x)].join(" ").toLowerCase().includes(q))
    );

  const [retrying, setRetrying] = useState<Record<number, boolean>>({});
  const retry = (item: any) => {
    setRetrying((prev) => ({ ...prev, [item.id]: true }));
    ApiService.NotificationController.Retry(item.id, (response: IAPIResponse) => {
      setRetrying((prev) => ({ ...prev, [item.id]: false }));
      if (response.error === null) {
        ToastMessage("success", "Email rimessa in coda.");
        setTimeout(() => load(true), 3000);
      } else {
        ToastMessage("error", "Impossibile reinviare l'email.");
      }
    });
  };

  const verdict = mailVerdict(mail, items);
  const badge = (item: any) => (item.event === "test-alarm" ? <Pill kind="warn" text="PROVA" title="Email di un allarme di prova" /> : null);
  const opened = (item: any) => (doorOpenings(item) ? <span className="sub"> · {doorOpenings(item)} aperture</span> : null);
  const stato = (item: any) => {
    const t = STATUS[item.status] ?? [item.status, "grey"];
    return (
      <>
        <Pill kind={t[1]} text={t[0]} />
        {item.attempts > 1 && <span className="sub"> {item.attempts} tentativi</span>}
        {item.lastError && (
          <span className="sub el" title={item.lastError}>
            {item.lastError}
          </span>
        )}
      </>
    );
  };
  const acts = (item: any) => (
    <div className="acts">
      <Btn text="Vedi" onClick={() => setViewed(item)} />
      {isNotSent(item) && (
        <Btn
          text="Reinvia"
          spin={!!retrying[item.id]}
          why={retrying[item.id] ? "Invio in corso" : mail && !mail.configured ? "La posta non è configurata: reinviare non servirebbe" : null}
          onClick={() => retry(item)}
        />
      )}
    </div>
  );

  const empty = (
    <Empty
      title={filter === "non" ? "Nessuna email ferma" : "Nessun invio"}
      text={filter === "non" ? "Tutte le email sono partite." : q ? "Cerca per destinatario o oggetto." : "Le email degli allarmi compariranno qui."}
      action={
        filter !== "tutti" || q ? (
          <Btn
            text="Mostra tutti"
            onClick={() => {
              setFilter("tutti");
              setSearch("");
            }}
          />
        ) : undefined
      }
    />
  );

  const w = props.width;
  const wide = w >= 1280;
  const mid = w >= 1015;
  const columns: ISetupColumn<any>[] = [
    {
      name: "Data",
      width: 112,
      hide: !mid,
      render: (l) => (
        <>
          {dayOf(l.createdAt)}
          <br />
          <span className="sub">{timeOf(l.createdAt)}</span>
        </>
      ),
    },
    {
      name: "Evento",
      width: wide ? 240 : mid ? 220 : 190,
      render: (l) => (
        <span className="wrap2" title={eventText(l)}>
          {eventText(l)} {badge(l)}
          {opened(l)}
        </span>
      ),
    },
    {
      name: "Destinatario",
      width: wide ? 220 : mid ? 240 : 220,
      render: (l) => (
        <>
          <span className="el" title={l.email}>
            {l.email}
          </span>
          {!mid && (
            <span className="sub el">
              {dayOf(l.createdAt)} {timeOf(l.createdAt)}
            </span>
          )}
        </>
      ),
    },
    {
      name: "Oggetto",
      hide: !wide,
      render: (l) => (
        <span className="el" title={l.subject}>
          {l.subject}
        </span>
      ),
    },
    { name: "Stato", width: wide ? 220 : mid ? 200 : 170, render: stato },
    { name: "", width: wide ? 184 : undefined, render: acts },
  ];

  const sender = mail ? (mail.fromName ? `${mail.fromName} <${mail.from}>` : mail.from ?? "") : "";

  return (
    <div>
      <PageHead title="Notifiche" line="Le email degli allarmi: se partono e lo storico degli invii." />
      {verdict && (
        <div className={"status " + (verdict.ok ? "ok" : "err")}>
          <Icon iconName={verdict.ok ? "CompletedSolid" : "ErrorBadge"} className="ico" />
          <span>
            {verdict.text}{" "}
            <span className="sub">· aggiornato alle {updatedAt}, si aggiorna da solo ogni 15 s</span>
          </span>
        </div>
      )}
      <Toolbar
        left={
          <Chips
            chips={[
              { key: "tutti", text: "Tutti" },
              { key: "prove", text: "Solo prove" },
              { key: "non", text: "Non partite" },
            ]}
            selected={filter}
            onChange={setFilter}
          />
        }
        shown={rows.length}
        total={items.length}
        noun="invii"
        search={search}
        onSearch={setSearch}
        placeholder="Cerca destinatario o oggetto"
      />
      {!loaded ? (
        <div className="empty">
          <span className="spinner" />
        </div>
      ) : w < CARDS_MAX_WIDTH ? (
        <SetupCards
          rows={rows}
          rowKey={(l) => l.id}
          empty={empty}
          card={(l) => (
            <div className="cd">
              <div className="h">
                <span className="el">{eventText(l)}</span>
                {badge(l)}
                {opened(l)}
              </div>
              <div className="r">
                <Pill kind={(STATUS[l.status] ?? ["", "grey"])[1]} text={(STATUS[l.status] ?? [l.status])[0]} />
              </div>
              <div className="l" style={{ gridColumn: "1" }}>
                <span className="el">{l.email}</span>
                <span className="sub el">
                  {dayOf(l.createdAt)} {timeOf(l.createdAt)}
                  {l.attempts > 1 ? " · " + l.attempts + " tentativi" : ""}
                  {l.lastError ? " · " + l.lastError : ""}
                </span>
              </div>
              <div className="r">{acts(l)}</div>
            </div>
          )}
        />
      ) : (
        <SetupTable columns={columns} rows={rows} rowKey={(l) => l.id} empty={empty} />
      )}
      {viewed && (
        <Dialog
          title={viewed.status === "sent" ? "Email inviata" : "Email"}
          ariaTitle="Email"
          wide
          onClose={() => setViewed(undefined)}
          footer={<Btn text="Chiudi" primary onClick={() => setViewed(undefined)} />}
        >
          <div className="email-prev">
            <div className="sub">
              {sender ? <div>Da: {sender}</div> : null}
              <div>A: {viewed.email}</div>
              <div>
                Oggetto: <b>{viewed.subject}</b> {badge(viewed)}
              </div>
              <div>
                {eventText(viewed)} · {(STATUS[viewed.status] ?? [viewed.status])[0]}
              </div>
            </div>
            <hr style={{ border: 0, borderTop: "1px solid #edebe9", margin: "8px 0" }} />
            {viewed.body ? (
              <div style={{ whiteSpace: "pre-wrap" }}>{viewed.body}</div>
            ) : (
              <div className="muted">Il testo dell'email non è arrivato dal server.</div>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default NotificationsTab;
